import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { toast } from "react-toastify";
import { Box, Typography, Avatar } from "@mui/material";
import { NotificationsActive } from "@mui/icons-material";
import SignalRService from "./SignalRService";
import { axiosPrivate } from "../axios/axios";
import { masterAPI } from "../Pages/Masters/dataConfig";
import { stripHtml } from "./stripHtml";
import { getNotificationColor } from "./notificationVisual";
import { getNotificationRoute } from "./notificationRoutes";
import useOnlineStatus from "./useOnlineStatus";

// No-UI component: keeps one SignalR connection alive app-wide instead of
// each page opening/closing its own connection.
const NotificationListener = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accessToken = useSelector((state) => state.auth.loginInfo.accessToken);
  const userId = useSelector((state) => state.auth.loginInfo.id);
  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (!accessToken || !userId) {
      SignalRService.stop();
      return;
    }

    let isMounted = true;
    let hasSignalRSetup = false;
    let notificationHandler = null;
    let reconnectHandler = null;

    const refreshNotifications = async (attempt = 0) => {
      if (!navigator.onLine) return;

      try {
        const [allRes, countRes] = await Promise.all([
          axiosPrivate.get(`${masterAPI["notificationLog"].getall}`),
          axiosPrivate.get(`${masterAPI["notificationLog"].getcount}`),
        ]);

        if (!isMounted) return;

        if (allRes?.data?.result) {
          dispatch({ type: "setNotifications", payload: allRes.data.result });
        }

        if (countRes?.data?.result?.count !== undefined) {
          dispatch({ type: "setUnreadCount", payload: countRes.data.result.count });
        }
      } catch (e) {
        console.log(e, "NotificationLog refresh error");
        if (isMounted && navigator.onLine && attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 1500 * (attempt + 1)));
          return refreshNotifications(attempt + 1);
        }
      }
    };

    const setup = async (attempt = 0) => {
      if (hasSignalRSetup) {
        return;
      }

      try {
        const connection = await SignalRService.start();
        if (!isMounted) return;

        hasSignalRSetup = true;

        if (connection.state === signalR.HubConnectionState.Connected) {
          console.log("SignalR connected, joining group", userId);
          await connection.invoke("JoinGroup", userId);
        }

        notificationHandler = async (data) => {
          console.log("SendNotification received:", data);
          dispatch({ type: "addNotification", payload: data });
          await refreshNotifications();

          const route = getNotificationRoute(data.notificationType, data.referenceId);
          toast(
            <Box
              onClick={() => route && navigate(route)}
              sx={{ display: "flex", gap: 1.2, cursor: route ? "pointer" : "default" }}
            >
              <Avatar sx={{ bgcolor: getNotificationColor(data.notificationType), width: 32, height: 32 }}>
                <NotificationsActive fontSize="small" />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                  New Notification
                </Typography>
              </Box>
            </Box>,
            { autoClose: 8000 },
          );
        };

        reconnectHandler = async () => {
          await connection.invoke("JoinGroup", userId);
          await refreshNotifications();
        };

        connection.on("SendNotification", notificationHandler);
        connection.onreconnected(reconnectHandler);
      } catch (err) {
        console.error("SignalR connection failed:", err);
        if (isMounted && navigator.onLine && attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 1500 * (attempt + 1)));
          return setup(attempt + 1);
        }
      }
    };

    if (isOnline) {
      setup();
      refreshNotifications();
    }

    return () => {
      isMounted = false;
      const connection = SignalRService.getConnection();
      if (connection) {
        if (notificationHandler) {
          connection.off("SendNotification", notificationHandler);
        }
        if (reconnectHandler) {
          connection.off("reconnected", reconnectHandler);
        }
        if (connection.state === signalR.HubConnectionState.Connected) {
          connection.invoke("LeaveGroup", userId).catch((e) => {
            console.log(e, "LeaveGroup error");
          });
        }
      }
    };
  }, [accessToken, userId, dispatch, navigate, isOnline]);

  return null;
};

export default NotificationListener;
